<script setup>
import {
  computed,
  ref,
  reactive,
  watch,
  onMounted,
  onActivated,
  onDeactivated,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import api from 'dashboard/api/wahaManager';
import { createQrLogin } from './waha/qrLogin';
import { createPairingLogin, normalizePairingPhone } from './waha/pairingLogin';
import { cache, setOwner, resetCache, refresh, invalidate } from './waha/cache';
import { connectionAlerts, connectionSnapshotStale } from './waha/connectionAlerts';
import {
  statusText,
  deletionText,
  onboardingText,
  historyText,
} from './waha/labels';

const route = useRoute();
const router = useRouter();
const user = useMapGetter('getCurrentUser');
const { isAdmin } = useAdmin();
const allowed = computed(
  () =>
    String(route.params.accountId) === '1' && isAdmin.value && !!user.value?.id
);
const owner = computed(() =>
  allowed.value ? `${route.params.accountId}:${user.value.id}` : ''
);
const proxyPage = computed(
  () =>
    route.name === 'settings_waha_proxies' ||
    ['#proxy-ip', '#proxies'].includes(route.hash)
);
const wizard = computed(() => route.hash === '#whatsapp-waha');
const kind = computed(() => (proxyPage.value ? 'proxies' : 'state'));
const slot = computed(() => cache[kind.value]);
const sessions = computed(() => cache.state.data?.sessions || []);
const healthNow = ref(Date.now());
const connectionWarnings = computed(() => connectionAlerts(sessions.value));
const connectionUnverified = computed(() => connectionSnapshotStale(cache.state, healthNow.value));
const proxies = computed(() =>
  proxyPage.value
    ? cache.proxies.data?.proxies || []
    : cache.state.data?.proxies || []
);
const search = ref('');
const matches = (item) =>
  [
    item.name,
    item.session_id,
    item.phone,
    item.host,
    item.exit_ip,
    item.assigned_name,
  ].some((value) =>
    String(value || '')
      .toLowerCase()
      .includes(search.value.toLowerCase())
  );
const rows = computed(() =>
  (proxyPage.value ? proxies.value : sessions.value).filter(matches)
);
const limit = computed(() => cache.state.data?.limit ?? 5);
const canAct = computed(
  () =>
    allowed.value &&
    (!wizard.value || !!selected.value) &&
    !!slot.value.data &&
    !!slot.value.updatedAt &&
    !slot.value.loading &&
    !slot.value.error &&
    !busy.value
);
const notice = ref('');
const busy = ref(false);
const creationUncertain = ref(false);
const availableProxies = computed(() =>
  proxies.value.filter(
    (proxy) => proxy.protocol === 'http' && !proxy.assigned_session_id &&
      !sessions.value.some((session) => session.proxy_id === proxy.id)
  )
);
const creationProxy = computed(() =>
  availableProxies.value.find((proxy) => proxy.id === form.proxy_id)
);
const proxyReady = (proxy) =>
  proxy?.status === 'AVAILABLE' && Number(proxy.tested_at) > 0 &&
  Date.now() / 1000 - Number(proxy.tested_at) < 600;
const selectedId = computed(() =>
  wizard.value && typeof route.query.session === 'string'
    ? route.query.session
    : ''
);
const selected = computed(() =>
  sessions.value.find((item) => String(item.id) === selectedId.value)
);
const powerControl = computed(() => {
  if (!selected.value?.status_error) {
    switch (selected.value?.status) {
      case 'STOPPED':
        return { label: '启动', action: 'start' };
      case 'FAILED':
        return { label: '启动', action: 'restart' };
      case 'STARTING':
      case 'SCAN_QR_CODE':
      case 'WORKING':
        return { label: '停止', action: 'stop' };
    }
  }
  return { label: '状态待确认', action: null };
});
const inboxName = ref('');
watch(
  () => selected.value?.id,
  () => {
    inboxName.value = selected.value ? `${selected.value.name}（WAHA）` : '';
  },
  { immediate: true }
);
const dialog = ref(null);
const modal = ref(null);
const form = reactive({});
const modalError = ref('');
const qrUrl = ref('');
const qrMessage = ref('');
const pairingPhone = ref('');
const pairingCode = ref('');
const pairingMessage = ref('');
const pairingBusy = ref(false);
let active = false,
  activationSequence = 0,
  timer,
  qrFlow,
  pairingFlow,
  epoch = 0;
const tableClass = 'w-full text-body-main text-left';
const cellClass =
  'px-3 py-4 border-b border-n-weak align-middle first:pl-0 last:pr-0';
const selectClass =
  'w-full h-10 mt-2 !mb-0 rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 px-3 py-2 focus:border-n-brand';
const date = (value) =>
  value
    ? new Date(value * 1000).toLocaleString('zh-CN', { hour12: false })
    : '—';
const proxyFor = (session) =>
  proxies.value.find((item) => item.id === session.proxy_id);
const sessionBlocked = (session) =>
  session.deletion || session.integration === 'DELETING';
const nav = (name, hash = '') =>
  router.push({ name, params: { accountId: route.params.accountId }, hash });
const inboxLink = (session, agents = false) =>
  router.push(
    `/app/accounts/${route.params.accountId}/settings/inboxes/${agents ? `new/${session.inbox_id}/agents` : session.inbox_id}`
  );
function clearQr() {
  pairingFlow?.stop();
  pairingFlow = undefined;
  pairingPhone.value = '';
  pairingCode.value = '';
  pairingMessage.value = '';
  pairingBusy.value = false;
  qrFlow?.stop();
  qrFlow = undefined;
  if (qrUrl.value) URL.revokeObjectURL(qrUrl.value);
  qrUrl.value = '';
  qrMessage.value = '';
}
function closeDialog() {
  epoch += 1;
  clearQr();
  modal.value = null;
  modalError.value = '';
  for (const key of Object.keys(form)) delete form[key];
}
function leave() {
  active = false;
  activationSequence += 1;
  epoch += 1;
  clearInterval(timer);
  clearQr();
  dialog.value?.close();
}
watch(
  () => cache.revision,
  () => {
    dialog.value?.close();
    closeDialog();
  }
);
watch(
  owner,
  (value, previous) => {
    setOwner(value);
    if (previous !== undefined && value !== previous) {
      dialog.value?.close();
      closeDialog();
      notice.value = '';
      if (active && value) refresh(kind.value);
    }
  },
  { immediate: true }
);
async function activate() {
  if (active) return;
  active = true;
  const activation = ++activationSequence;
  setOwner(owner.value);
  if (allowed.value) await refresh(kind.value);
  if (!active || activation !== activationSequence) return;
  timer = setInterval(() => {
    healthNow.value = Date.now();
    if (allowed.value && !document.hidden && !modal.value && !busy.value)
      refresh(kind.value);
  }, 12000);
}
onMounted(activate);
onActivated(activate);
onDeactivated(leave);
onBeforeUnmount(leave);
watch(
  () => [route.name, route.hash, selectedId.value],
  () => {
    if (!active) return;
    if (!['settings_waha', 'settings_waha_proxies'].includes(route.name)) {
      leave();
      return;
    }
    search.value = '';
    notice.value = '';
    dialog.value?.close();
    closeDialog();
    if (allowed.value) refresh(kind.value);
  },
  { flush: 'sync' }
);
const currentGuard = () => {
  const key = owner.value,
    version = epoch,
    session = selectedId.value;
  return () =>
    active &&
    allowed.value &&
    key === owner.value &&
    version === epoch &&
    session === selectedId.value;
};
function revokeAccess(error) {
  if (![401, 403].includes(error.response?.status)) return;
  resetCache(owner.value);
  cache[kind.value].error = '登录或管理权限已失效，请重新登录或联系管理员。';
  dialog.value?.close();
}
async function runAction(action, success = '操作已完成。', inDialog = false) {
  if (!canAct.value) return;
  busy.value = true;
  const operationOwner = owner.value;
  notice.value = '';
  modalError.value = '';
  const valid = currentGuard();
  try {
    await action(valid);
    if (operationOwner !== owner.value) return;
    invalidate();
    if (!valid()) return;
    if (inDialog) dialog.value?.close();
    notice.value = success;
    await refresh(kind.value);
  } catch (error) {
    if (!valid()) return;
    revokeAccess(error);
    if (!valid()) return;
    const message =
      error.response?.data?.error ||
      '请求结果未确认，请先刷新状态核对，不要重复提交。';
    if (inDialog) modalError.value = message;
    else notice.value = message;
  } finally {
    busy.value = false;
  }
}
async function openDialog(config, values = {}) {
  if (!canAct.value) return;
  clearQr();
  epoch += 1;
  modal.value = config;
  modalError.value = '';
  for (const key of Object.keys(form)) delete form[key];
  Object.assign(form, values);
  await nextTick();
  dialog.value?.open();
}
function confirmAction(title, description, action, danger = false) {
  openDialog({ title, description, action, danger, button: '确认' });
}
function proxyForm(proxy) {
  if (proxy?.assigned_session_id) return;
  openDialog(
    {
      title: proxy ? '编辑代理' : '添加代理 IP',
      type: 'proxy',
      button: '保存代理',
      description:
        '仅支持固定公网 IPv4。用户名和密码由后端加密保存；编辑时留空保留原凭据。',
      action: () =>
        api.saveProxy(proxy?.id, { ...form, port: Number(form.port) }),
    },
    {
      name: proxy?.name || '',
      host: proxy?.host || '',
      port: proxy?.port || '',
      protocol: proxy?.protocol || 'http',
      username: '',
      password: '',
    }
  );
}
function sessionForm() {
  openDialog(
    {
      title: '添加 WhatsApp',
      type: 'session',
      button: '添加账号',
      description: '为新账号选择一个独占的 HTTP 代理。添加后，在账号管理中完成扫码或手机号配对。',
      success: '账号已添加，请点击“管理”查看开通进度并连接 WhatsApp。',
      action: async () => {
        if (creationUncertain.value || sessions.value.length >= limit.value ||
            !form.name?.trim() || !proxyReady(creationProxy.value)) {
          throw { response: { data: { error: '请核对账号容量，并先测试所选代理。' } } };
        }
        try {
          await api.createSession({ name: form.name.trim(), proxy_id: form.proxy_id });
        } catch (error) {
          // A lost response may follow a committed creation. Never retry it automatically.
          if (!error.response || error.response.status >= 500) creationUncertain.value = true;
          throw error;
        }
      },
    },
    { name: '', proxy_id: '' }
  );
}
function deleteAccount(session) {
  if (session.deletion) {
    openDialog({
      title: `删除进度 · ${session.name}`,
      type: 'progress',
      sessionId: session.id,
      button: '',
      description:
        '全部清理完成后账号才会从列表移除；清理期间不可启动、扫码或重新绑定。',
    });
    return;
  }
  const snapshot = { ...session };
  openDialog(
    {
      title: '彻底删除 WhatsApp 账号？',
      type: 'delete',
      danger: true,
      button: '彻底删除',
      session: snapshot,
      description:
        '将删除本站专属 WAHA 节点、登录数据、媒体缓存与账号配置。重新连接需要扫码。',
      action: () =>
        api.deleteAccount(snapshot.id, form.confirmation, snapshot.inbox_id),
    },
    { confirmation: '' }
  );
}
const progressSession = computed(() =>
  sessions.value.find((item) => item.id === modal.value?.sessionId)
);
function retryDeletion() {
  const session = progressSession.value;
  if (!session || session.deletion?.status !== 'NEEDS_REVIEW') return;
  confirmAction(
    '继续删除此账号？',
    '重新核对归属，从保留进度继续清理，不会启动 WhatsApp。',
    () => api.retryDeletion(session.id, session.session_id),
    true
  );
}
const invalidForm = computed(() => {
  if (modal.value?.type === 'session')
    return creationUncertain.value || sessions.value.length >= limit.value ||
      !form.name?.trim() || !proxyReady(creationProxy.value);
  if (modal.value?.type === 'delete')
    return form.confirmation !== modal.value.session.session_id;
  if (modal.value?.type === 'proxy')
    return (
      !form.name?.trim() ||
      !form.host?.trim() ||
      !(Number(form.port) > 0 && Number(form.port) <= 65535)
    );
  if (modal.value?.type === 'binding')
    return !form.value;
  return false;
});
function submitDialog() {
  if (invalidForm.value || !modal.value?.action) return;
  return runAction(modal.value.action, modal.value.success || '请求已完成，请以最新状态为准。', true);
}
async function bindInbox(session) {
  if (sessionBlocked(session)) return;
  await openDialog(
    {
      title: '绑定已有 API 收件箱',
      type: 'binding',
      options: [],
      button: '保存映射',
      description:
        '只保存一对一映射，不会自动启用消息转发。已有邮箱和 WhatsApp Cloud 收件箱不可用。',
      action: () => api.bindInbox(session.id, form.value),
    },
    { value: '' }
  );
  const valid = currentGuard();
  try {
    const data = await api.chatwoot();
    if (valid()) modal.value.options = data.inboxes;
  } catch (error) {
    if (!valid()) return;
    revokeAccess(error);
    if (valid()) modalError.value = '无法读取可用收件箱，请重新打开后重试。';
  }
}
function togglePower(session) {
  if (!canAct.value || !powerControl.value.action || selected.value?.id !== session.id)
    return;
  sessionAction(session, powerControl.value.action);
}
function sessionAction(session, action) {
  if (sessionBlocked(session) || session.provisioning !== 'READY') return;
  const descriptions = {
    start: '启动当前账号的独立 Session，不更换代理。',
    restart: '重启会短暂断开连接，但不主动退出登录。',
    stop: '停止连接，保留登录数据。',
    logout: '退出登录会解除设备关联并清除 WAHA 登录信息，之后需要重新扫码。',
  };
  confirmAction(
    {
      start: '启动账号',
      restart: '重启账号',
      stop: '停止账号',
      logout: '退出登录',
    }[action],
    descriptions[action],
    () => api.action(session.id, action),
    action === 'logout'
  );
}
async function showQr(session) {
  if (sessionBlocked(session) || session.provisioning !== 'READY') return;
  const opening = openDialog({
    title: `扫码连接 · ${session.name}`,
    type: 'qr',
    button: '',
  });
  const valid = currentGuard();
  await opening;
  if (!valid() || modal.value?.type !== 'qr') return;
  qrFlow = createQrLogin({
    api,
    id: session.id,
    valid,
    image: (blob) => {
      if (qrUrl.value) URL.revokeObjectURL(qrUrl.value);
      qrUrl.value = blob ? URL.createObjectURL(blob) : '';
    },
    message: (text) => { qrMessage.value = text; },
    error: revokeAccess,
    connected: () => refresh('state'),
  });
  await qrFlow.start();
}
async function showPairing(session) {
  if (!canAct.value || session.id !== selected.value?.id ||
      sessionBlocked(session) || session.provisioning !== 'READY') return;
  await openDialog({ title: `手机号配对 · ${session.name}`, type: 'pairing', button: '' });
}
async function requestPairing() {
  const session = selected.value;
  if (!canAct.value || !session || pairingBusy.value || modal.value?.type !== 'pairing' ||
      sessionBlocked(session) || session.provisioning !== 'READY') return;
  const phone = normalizePairingPhone(pairingPhone.value);
  if (!phone) { pairingMessage.value = '请输入含国家区号的有效手机号。'; return; }
  pairingFlow?.stop();
  pairingBusy.value = true;
  pairingPhone.value = '';
  pairingCode.value = '';
  pairingFlow = createPairingLogin({
    api, id: session.id, phone, valid: currentGuard(),
    code: (value) => { pairingCode.value = value; },
    message: (value) => { pairingMessage.value = value; },
    error: revokeAccess,
    done: () => { pairingBusy.value = false; },
    connected: () => refresh('state'),
  });
  await pairingFlow.start();
}
function manage(session) {
  router.push({
    name: 'settings_waha',
    params: { accountId: route.params.accountId },
    query: { session: String(session.id) },
    hash: '#whatsapp-waha',
  });
}
</script>

<template>
  <SettingsLayout class="min-w-0 text-n-slate-12">
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="search"
        :title="
          proxyPage
            ? '代理 IP'
            : wizard
              ? '管理 WhatsApp（WAHA）'
              : 'WhatsApp（WAHA）'
        "
        :description="
          proxyPage
            ? '管理 WhatsApp 专属代理。先添加并测试代理，再连接账号；已绑定的代理不能编辑或删除。'
            : '管理 WhatsApp 账号连接、专属收件箱与历史同步。每个账号使用独占代理，连接状态与消息转发状态分别验证。'
        "
        :search-placeholder="
          wizard
            ? ''
            : proxyPage
              ? '搜索代理名称 / IP'
              : '搜索名称 / Session / 号码'
        "
      >
        <template #title>
          <div class="flex flex-wrap items-center justify-between w-full gap-2">
            <h1 class="text-heading-1 text-n-slate-12">
              {{
                proxyPage
                  ? '代理 IP'
                  : wizard
                    ? '管理 WhatsApp（WAHA）'
                    : 'WhatsApp（WAHA）'
              }}
            </h1>
            <div class="flex flex-wrap gap-1">
              <Button
                v-if="wizard && allowed"
                size="sm"
                label="刷新"
                icon="i-lucide-refresh-cw"
                variant="ghost"
                color="slate"
                :disabled="slot.loading || busy"
                @click="refresh(kind)"
              />
              <Button
                size="sm"
                v-if="wizard"
                label="返回账号列表"
                variant="ghost"
                color="slate"
                @click="nav('settings_waha')"
              />
            </div>
          </div>
        </template>
        <template v-if="slot.data && !wizard && allowed" #count>
          <span
            class="hidden lg:inline text-body-main text-n-slate-11 whitespace-nowrap"
          >
            {{
              proxyPage
                ? `${proxies.length} 个代理`
                : `${sessions.length} / ${limit} 个账号 · ${sessions.filter((s) => s.status === 'WORKING').length} 在线 · ${proxies.length} 个代理`
            }}
          </span>
        </template>
        <template v-if="allowed && !wizard" #actions>
          <span v-if="slot.loading && slot.data" role="status" class="sr-only"
            >后台更新中…</span
          >
          <Button
            size="sm"
            label="刷新"
            icon="i-lucide-refresh-cw"
            variant="ghost"
            color="slate"
            :disabled="slot.loading || busy"
            @click="refresh(kind)"
          />
          <Button
            v-if="proxyPage"
            size="sm"
            label="添加代理"
            icon="i-lucide-plus"
            :disabled="!canAct"
            @click="proxyForm()"
          />
          <Button
            v-else
            size="sm"
            label="添加 WhatsApp"
            icon="i-lucide-plus"
            :disabled="!canAct"
            @click="sessionForm()"
          />
        </template>
      </BaseSettingsHeader>
      <Input
        v-if="!wizard && allowed"
        v-model="search"
        :placeholder="
          proxyPage ? '搜索代理名称 / IP' : '搜索名称 / Session / 号码'
        "
        size="sm"
        type="search"
        class="sm:hidden w-full"
      />
    </template>
    <template #body>
      <div class="flex flex-col min-w-0 gap-4">
        <p v-if="!allowed" role="alert">仅账户 1 的管理员可以管理 WhatsApp。</p>
        <template v-else>
          <section
            v-if="!proxyPage && (connectionWarnings.length || connectionUnverified)"
            role="status"
            aria-live="polite"
            aria-label="WhatsApp 连接告警"
            class="rounded-lg p-4 border border-n-amber-6 bg-n-amber-2 text-sm"
          >
            <h2 class="font-semibold text-n-amber-11">WhatsApp 连接需要处理</h2>
            <p v-if="connectionUnverified" class="mt-2">
              连接状态尚未重新确认，以下可能是上次读取的结果。请刷新后再判断是否恢复。
            </p>
            <ul v-if="connectionWarnings.length" class="flex flex-col gap-3 mt-3">
              <li v-for="warning in connectionWarnings" :key="warning.id" class="flex flex-wrap items-center gap-2">
                <span class="font-medium">{{ warning.name }}</span>
                <span>{{ warning.message }}</span>
                <Button label="管理连接" variant="link" size="sm" @click="manage(warning.session)" />
              </li>
            </ul>
            <p class="mt-3 text-n-slate-11">告警会在确认恢复后自动消失。已有“投递待核对”消息不会自动重发。</p>
          </section>
          <p
            v-if="notice"
            role="status"
            class="rounded-lg p-3 bg-n-alpha-2 text-sm"
          >
            {{ notice }}
          </p>
          <div
            v-if="slot.error"
            role="alert"
            class="rounded-lg p-3 border border-n-ruby-6 text-n-ruby-11 text-sm"
          >
            {{ slot.error }}
            <Button
              label="重试"
              variant="link"
              :disabled="slot.loading"
              @click="refresh(kind)"
            />
          </div>
          <div
            v-if="!slot.data && slot.loading"
            role="status"
            aria-label="首次加载"
            class="flex flex-col gap-3"
          >
            <div
              v-for="n in 4"
              :key="n"
              class="h-12 rounded-lg bg-n-alpha-2 animate-pulse"
            />
            <span class="text-sm text-n-slate-11">正在加载…</span>
          </div>
          <template v-if="slot.data">
            <template v-if="wizard && !proxyPage">
              <div
                class="flex flex-col gap-5 max-w-3xl pt-4 border-t border-n-weak"
              >
                <template v-if="selected">
                  <h2 class="text-base font-medium m-0">
                    {{ selected.name }} · {{ selected.session_id }}
                  </h2>
                  <p class="m-0 text-sm">
                    {{
                      selected.deletion
                        ? deletionText(selected)
                        : statusText(selected.status)
                    }}
                    · 专属代理：{{ proxyFor(selected)?.name || '未绑定' }} ·
                    出口：{{ proxyFor(selected)?.exit_ip || '尚未验证' }}
                  </p>
                  <p
                    v-if="
                      selected.provisioning !== 'READY' || selected.onboarding
                    "
                    class="m-0 text-sm"
                  >
                    {{ onboardingText(selected) }}
                  </p>
                  <p class="m-0 text-sm text-n-slate-11">
                    {{ historyText(selected) }}
                  </p>
                  <p
                    v-if="selected.integration_error || selected.status_error"
                    role="alert"
                    class="text-n-ruby-11"
                  >
                    {{ selected.integration_error || selected.status_error }}
                  </p>
                  <p v-if="sessionBlocked(selected)" role="status">
                    账号或收件箱正在清理，暂时不能启动、扫码或重新绑定。
                  </p>
                  <div
                    v-if="!sessionBlocked(selected)"
                    class="flex flex-wrap gap-2"
                  >
                    <template v-if="selected.provisioning === 'READY'"
                      ><Button
                        label="扫码 / 查看连接"
                        :disabled="!canAct"
                        @click="showQr(selected)" /><Button
                        label="手机号配对"
                        :disabled="!canAct || selected.provisioning !== 'READY'"
                        @click="showPairing(selected)" /><Button
                        :label="powerControl.label"
                        variant="faded"
                        color="slate"
                        :disabled="!canAct || !powerControl.action"
                        @click="togglePower(selected)" /><Button
                        v-for="[action, text] in [
                          ['restart', '重启'],
                          ['logout', '退出登录'],
                        ]"
                        :key="action"
                        :label="text"
                        variant="faded"
                        :color="action === 'logout' ? 'ruby' : 'slate'"
                        :disabled="!canAct"
                        @click="sessionAction(selected, action)"
                    /></template>
                  </div>
                  <template v-if="!sessionBlocked(selected)">
                    <div v-if="selected.inbox_id" class="flex flex-wrap gap-2">
                      <span class="text-sm"
                        >关联收件箱：{{ selected.inbox_name }} · #{{
                          selected.inbox_id
                        }}</span
                      ><Button
                        label="分配客服 / 收件箱设置"
                        variant="link"
                        @click="inboxLink(selected)"
                      /><Button
                        v-if="
                          !selected.messaging_enabled && !selected.onboarding
                        "
                        label="下一步：选择客服"
                        variant="link"
                        @click="inboxLink(selected, true)"
                      />
                    </div>
                    <template v-else-if="!selected.onboarding"
                      ><Input v-model="inboxName" label="新收件箱名称" />
                      <div class="flex gap-2">
                        <Button
                          label="创建并绑定 API 收件箱"
                          :disabled="
                            !canAct ||
                            selected.status !== 'WORKING' ||
                            proxyFor(selected)?.status !== 'AVAILABLE' ||
                            !inboxName.trim()
                          "
                          @click="
                            runAction(
                              () => api.createInbox(selected.id, inboxName),
                              '收件箱已创建，请继续分配客服。'
                            )
                          "
                        /><Button
                          label="绑定已有收件箱"
                          variant="faded"
                          color="slate"
                          :disabled="!canAct"
                          @click="bindInbox(selected)"
                        /></div
                    ></template>
                    <p v-else class="m-0 text-sm text-n-slate-11">
                      自动开通流程会创建并绑定收件箱，无需重复创建。创建账号的管理员默认加入。
                    </p>
                  </template>
                  <Button
                    :label="selected.deletion ? '查看删除进度' : '删除账号'"
                    class="self-start"
                    color="ruby"
                    variant="faded"
                    :disabled="!canAct"
                    @click="deleteAccount(selected)"
                  />
                </template>
                <p v-else class="text-sm text-n-slate-11">
                  {{ selectedId ? '该账号不存在或已不可管理，请返回账号列表。' : '请从账号列表点击对应账号的“管理”进入。' }}
                </p>
              </div>
            </template>
            <template v-else>
              <p
                v-if="!rows.length"
                class="m-0 border-t border-n-weak py-20 text-center text-body-main text-n-slate-11"
              >
                {{
                  search
                    ? '没有匹配的结果'
                    : proxyPage
                      ? '还没有代理 IP，点击“添加代理”开始配置。'
                      : '还没有 WhatsApp 账号，先添加代理，再添加账号。'
                }}
              </p>
              <div v-else class="overflow-x-auto border-t border-n-weak">
                <table
                  :class="[
                    tableClass,
                    proxyPage ? 'min-w-[760px]' : 'min-w-[960px]',
                  ]"
                >
                  <thead class="text-n-slate-11">
                    <tr>
                      <th
                        v-for="heading in proxyPage
                          ? [
                              '代理 / 地址',
                              '出口 / 国家',
                              '状态',
                              '绑定账号',
                              '延迟',
                              '操作',
                            ]
                          : [
                              '名称 / Session',
                              '号码',
                              '状态',
                              '代理 / 出口',
                              '收件箱',
                              '最近在线',
                              '操作',
                            ]"
                        :key="heading"
                        scope="col"
                        class="px-3 py-3 font-medium whitespace-nowrap border-b border-n-weak first:pl-0 last:pr-0"
                      >
                        {{ heading }}
                      </th>
                    </tr>
                  </thead>
                  <tbody v-if="proxyPage">
                    <tr
                      v-for="p in rows"
                      :key="p.id"
                      class="hover:bg-n-alpha-1"
                    >
                      <td :class="cellClass">
                        <div class="text-heading-3 break-words">
                          {{ p.name }}
                        </div>
                        <div class="text-xs text-n-slate-11">
                          {{ p.protocol.toUpperCase() }} {{ p.host }}:{{
                            p.port
                          }}
                        </div>
                      </td>
                      <td :class="cellClass">
                        {{ p.exit_ip || '尚未验证' }}
                        <div class="text-xs text-n-slate-11">
                          {{ p.country || '—' }}
                        </div>
                      </td>
                      <td :class="cellClass">{{ statusText(p.status) }}</td>
                      <td :class="cellClass">
                        {{
                          p.assigned_name ||
                          (p.assigned_session_id ? '已绑定' : '未绑定')
                        }}
                      </td>
                      <td :class="cellClass">
                        {{ p.latency_ms == null ? '—' : `${p.latency_ms} ms` }}
                      </td>
                      <td :class="cellClass">
                        <div class="flex gap-1">
                          <Button
                            size="sm"
                            label="测试"
                            variant="ghost"
                            color="slate"
                            :disabled="!canAct"
                            @click="
                              runAction(
                                () => api.testProxy(p.id),
                                '代理连接测试完成；不代表 WhatsApp 全链路隔离验收。'
                              )
                            "
                          /><Button
                            size="sm"
                            label="编辑"
                            variant="ghost"
                            color="slate"
                            :disabled="!canAct || !!p.assigned_session_id"
                            @click="proxyForm(p)"
                          /><Button
                            size="sm"
                            label="删除"
                            variant="ghost"
                            color="ruby"
                            :disabled="!canAct || !!p.assigned_session_id"
                            @click="
                              confirmAction(
                                '删除未绑定代理？',
                                '该代理配置和凭证将从管理后台删除。',
                                () => api.deleteProxy(p.id),
                                true
                              )
                            "
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr
                      v-for="s in rows"
                      :key="s.id"
                      class="hover:bg-n-alpha-1"
                    >
                      <td :class="cellClass">
                        <div class="text-heading-3 break-words">
                          {{ s.name }}
                        </div>
                        <div class="text-xs text-n-slate-11">
                          {{ s.session_id }}
                        </div>
                      </td>
                      <td :class="cellClass">
                        {{ s.phone ? `+${s.phone}` : '未连接' }}
                      </td>
                      <td :class="cellClass">
                        <div>
                          {{
                            s.deletion ? deletionText(s) : statusText(s.status)
                          }}
                        </div>
                        <div class="text-xs text-n-slate-11">
                          {{
                            s.deletion?.error ||
                            s.integration_error ||
                            s.status_error ||
                            (s.provisioning !== 'READY'
                              ? onboardingText(s)
                              : '')
                          }}
                        </div>
                      </td>
                      <td :class="cellClass">
                        {{ proxyFor(s)?.name || '未绑定' }}
                        <div class="text-xs text-n-slate-11">
                          {{ proxyFor(s)?.exit_ip || '—' }} ·
                          {{ proxyFor(s)?.country || '—' }}
                        </div>
                      </td>
                      <td :class="cellClass">
                        {{ s.inbox_name || '未绑定' }}
                        <div v-if="s.inbox_id" class="text-xs text-n-slate-11">
                          {{
                            s.messaging_enabled
                              ? '转发已启用'
                              : '待启用消息转发'
                          }}
                        </div>
                      </td>
                      <td :class="cellClass" class="whitespace-nowrap">
                        {{ date(s.last_online) }}
                      </td>
                      <td :class="cellClass">
                        <div class="flex gap-1">
                          <Button
                            size="sm"
                            label="管理"
                            variant="ghost"
                            color="slate"
                            :disabled="!canAct || !!s.deletion"
                            @click="manage(s)"
                          /><Button
                            size="sm"
                            :label="s.deletion ? '删除进度' : '删除'"
                            variant="ghost"
                            color="ruby"
                            :disabled="!canAct"
                            @click="deleteAccount(s)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </template>
        </template>
      </div>
    </template>
    <Dialog
      ref="dialog"
      :title="modal?.title"
      :description="modal?.description"
      :type="modal?.danger ? 'alert' : 'edit'"
      :confirm-button-label="modal?.button"
      :show-confirm-button="!!modal?.button"
      :disable-confirm-button="!canAct || invalidForm"
      :is-loading="busy"
      cancel-button-label="关闭"
      overflow-y-auto
      @confirm="submitDialog"
      @close="closeDialog"
    >
      <div v-if="modal" class="flex flex-col gap-4 text-sm">
        <p v-if="modalError" role="alert" class="text-n-ruby-11 m-0">
          {{ modalError }}
        </p>
        <template v-if="modal.type === 'session'">
          <p v-if="creationUncertain" role="alert" class="m-0 text-n-ruby-11">
            添加结果未确认，已停止重复提交。请关闭弹窗、刷新账号列表核对；若仍未出现，请联系管理员检查创建状态。
          </p>
          <p v-if="sessions.length >= limit" role="alert" class="m-0 text-n-ruby-11">
            已达到 {{ limit }} 个账号上限。
          </p>
          <Input v-model="form.name" label="账号名称" placeholder="例如：售后客服" :disabled="busy" />
          <label>专属代理
            <select v-model="form.proxy_id" :class="selectClass" :disabled="busy">
              <option value="">请选择未绑定的 HTTP 代理</option>
              <option v-for="proxy in availableProxies" :key="proxy.id" :value="proxy.id">
                {{ proxy.name }} · {{ statusText(proxy.status) }}
              </option>
            </select>
          </label>
          <div class="flex flex-wrap gap-2">
            <Button label="测试所选代理" variant="faded" color="slate"
              :disabled="!canAct || !creationProxy"
              @click="runAction(() => api.testProxy(form.proxy_id), '代理测试完成，请继续添加账号。')" />
            <Button label="管理代理 IP" variant="link" :disabled="busy"
              @click="nav('settings_waha_proxies')" />
          </div>
          <p class="m-0 text-n-slate-11">
            {{ availableProxies.length ? '请先测试所选代理，成功结果有效期为 10 分钟。每个账号使用独立出口。' : '暂无未绑定的 HTTP 代理，请先到“管理代理 IP”添加代理。' }}
          </p>
        </template>
        <template v-else-if="modal.type === 'proxy'"
          ><Input v-model="form.name" label="名称" :disabled="busy" /><label
            >协议<select
              v-model="form.protocol"
              :class="selectClass"
              :disabled="busy"
            >
              <option value="http">HTTP</option>
              <option value="socks5">SOCKS5</option>
            </select></label
          ><Input
            v-model="form.host"
            label="主机（固定公网 IPv4）"
            :disabled="busy" /><Input
            v-model="form.port"
            label="端口"
            type="number"
            :disabled="busy" /><Input
            v-model="form.username"
            label="代理账号（编辑时留空保留）"
            :disabled="busy" /><Input
            v-model="form.password"
            label="代理密码（编辑时留空保留）"
            type="password"
            :disabled="busy"
        /></template>
        <template v-else-if="modal.type === 'delete'"
          ><p class="text-n-ruby-11 m-0">
            {{
              modal.session.inbox_id
                ? `同时删除收件箱“${modal.session.inbox_name}”（#${modal.session.inbox_id}）、会话及关联任务。`
                : '当前没有关联收件箱；若提交前绑定发生变化，系统会拒绝并要求重新确认。'
            }}
          </p>
          <p class="m-0">
            保留代理配置并解除占用。不注销手机号的 WhatsApp
            账号，不删除手机聊天；手机端关联设备可能仍需手动移除。现有备份按原策略保留。
          </p>
          <Input
            v-model="form.confirmation"
            :label="`输入完整 Session 确认：${modal.session.session_id}`"
            :disabled="busy"
        /></template>
        <template v-else-if="modal.type === 'progress'"
          ><p role="status">
            {{
              progressSession
                ? deletionText(progressSession)
                : '账号已从列表移除。'
            }}
          </p>
          <p
            v-if="progressSession?.deletion?.error"
            role="alert"
            class="text-n-ruby-11"
          >
            {{ progressSession.deletion.error }}
          </p>
          <Button
            label="刷新进度"
            :disabled="cache.state.loading || busy"
            @click="refresh('state')" /><Button
            v-if="progressSession?.deletion?.status === 'NEEDS_REVIEW'"
            label="重试清理"
            color="ruby"
            :disabled="!canAct"
            @click="retryDeletion"
        /></template>
        <template
          v-else-if="modal.type === 'binding'"
          ><label
            >API 收件箱<select
              v-model="form.value"
              :class="selectClass"
              :disabled="busy"
            >
              <option value="">请选择</option>
              <option
                v-for="item in modal.options"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }} · {{ item.host || `#${item.id}` }}
              </option>
            </select></label
          ></template
        >
        <template v-else-if="modal.type === 'pairing'">
          <p class="m-0">输入已登录 WhatsApp 的手机号，包含国家区号。生成配对码后，在手机 WhatsApp 的“已关联设备”中选择“通过电话号码关联”，输入下方配对码。</p>
          <Input v-if="!pairingBusy" v-model="pairingPhone" type="tel"
            label="WhatsApp 手机号（含国家区号）" placeholder="+国家区号 手机号"
            autocomplete="off" :disabled="!canAct" />
          <Button v-if="!pairingBusy" label="获取配对码"
            :disabled="!canAct || !normalizePairingPhone(pairingPhone)" @click="requestPairing" />
          <div v-if="pairingCode" class="rounded-lg bg-n-alpha-2 p-4 text-center font-mono text-2xl tracking-widest select-text"
            aria-label="WhatsApp 配对码">{{ pairingCode }}</div>
          <p role="status" class="m-0">{{ pairingMessage }}</p>
          <Button label="改用扫码" variant="faded" @click="showQr(selected)" />
        </template>
        <template v-else-if="modal.type === 'qr'"
          ><img
            v-if="qrUrl"
            :src="qrUrl"
            alt="WhatsApp 关联设备二维码"
            class="w-64 h-64 mx-auto"
          />
          <p role="status">{{ qrMessage || '正在读取连接状态…' }}</p></template
        >
      </div>
    </Dialog>
  </SettingsLayout>
</template>
